import React, { useEffect, useMemo, useRef, useState } from "react";
import { Anchor, Box, Button, Group, Image, Stack, Text } from "@mantine/core";
import { useLocation, useParams } from "react-router-dom";
import QRCode from "qrcode";
import "./app.css";

/**
 * Landing page for WhatsApp 1-tap login links: https://captivadata.app/l/:nonce?app=fuel|flex|trip
 *
 * WhatsApp only makes http(s) URLs tappable, so the bot sends this page instead of a custom
 * scheme. The page never redeems or reads the nonce server-side (so link previews can't burn
 * it); it only hands it to the native app via `captiva{app}://l?nonce=...`, where the app
 * redeems it through the `redeemLoginNonce` callable.
 *
 * TODO: replace with verified Universal Links / App Links (AASA + assetlinks.json + app
 * associatedDomains/intentFilters) once the Apple Team ID and Android signing fingerprints
 * are wired in. The custom-scheme bounce is interim.
 */

export type CaptivaApp = "fuel" | "flex" | "trip";

export const LOGIN_LINK_APPS: Record<
  CaptivaApp,
  { label: string; scheme: string; appleUrl: string; googleUrl: string }
> = {
  fuel: {
    label: "Captiva Fuel",
    scheme: "captivafuel",
    appleUrl: "https://apps.apple.com/us/app/captiva-fuel/id6480343590",
    googleUrl: "https://play.google.com/store/apps/details?id=com.captiva.fuel",
  },
  flex: {
    label: "Captiva Flex",
    scheme: "captivaflex",
    appleUrl: "https://apps.apple.com/us/app/captiva-flex/id6752111609",
    googleUrl: "https://play.google.com/store/apps/details?id=com.captiva.flex",
  },
  trip: {
    label: "Captiva Trip",
    scheme: "captivatrip",
    appleUrl: "https://apps.apple.com/us/app/captiva-trip/id6740551915",
    googleUrl: "https://play.google.com/store/apps/details?id=com.captiva.trip",
  },
};

export function resolveLoginLinkApp(raw: string | null): CaptivaApp {
  return raw === "flex" || raw === "trip" || raw === "fuel" ? raw : "fuel";
}

export function buildAppLoginUrl(app: CaptivaApp, nonce: string): string {
  return `${LOGIN_LINK_APPS[app].scheme}://l?nonce=${encodeURIComponent(nonce)}`;
}

function detectPlatform(): "ios" | "android" | "desktop" {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  if (/android/i.test(ua)) return "android";
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  return "desktop";
}

export function LoginLinkLanding() {
  const { nonce = "" } = useParams<{ nonce: string }>();
  const location = useLocation();
  const app = resolveLoginLinkApp(new URLSearchParams(location.search).get("app"));
  const config = LOGIN_LINK_APPS[app];
  const platform = useMemo(detectPlatform, []);
  const appUrl = nonce ? buildAppLoginUrl(app, nonce) : "";
  const [qrDataUrl, setQrDataUrl] = useState<string>("");
  const attemptedRef = useRef(false);

  // On a phone, try to open the app straight away (one attempt; the button is the fallback).
  useEffect(() => {
    if (!appUrl || platform === "desktop" || attemptedRef.current) return;
    attemptedRef.current = true;
    window.location.href = appUrl;
  }, [appUrl, platform]);

  // On desktop (e.g. WhatsApp Web), show a QR code of this page to continue on the phone.
  useEffect(() => {
    if (platform !== "desktop" || !nonce) return;
    QRCode.toDataURL(window.location.href, { margin: 1, width: 220 })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(""));
  }, [platform, nonce]);

  if (!nonce) {
    return (
      <Box className="center" sx={{ maxWidth: 360 }} mx="auto">
        <Stack spacing="xs" align="center">
          <Text size="lg">This login link is incomplete.</Text>
          <Text size="sm" color="dimmed">
            Request a new link from the Captiva WhatsApp assistant.
          </Text>
        </Stack>
      </Box>
    );
  }

  return (
    <Box className="center" sx={{ maxWidth: 360 }} mx="auto">
      <Stack spacing="sm" align="center">
        <Image
          radius="md"
          src="/captiva-mark.png"
          style={{ height: "6em", width: "6em", margin: "auto" }}
        />
        <Text size="lg" weight={600}>
          Log in to {config.label}
        </Text>

        {platform === "desktop" ? (
          <>
            <Text size="sm" align="center">
              Open this link on the phone where {config.label} is installed. Scan the code
              below with your phone camera:
            </Text>
            {qrDataUrl && (
              <img src={qrDataUrl} alt={`QR code to open ${config.label}`} width={220} height={220} />
            )}
          </>
        ) : (
          <>
            <Button
              component="a"
              href={appUrl}
              color="cap-navy"
              size="md"
              style={{ width: "100%" }}
            >
              Open {config.label}
            </Button>
            <Text size="sm" color="dimmed" align="center">
              Don't have the app yet? Install it, then tap your WhatsApp link again.
            </Text>
            <Group position="center" spacing="md">
              {platform !== "android" && <Anchor href={config.appleUrl}>App Store</Anchor>}
              {platform !== "ios" && <Anchor href={config.googleUrl}>Google Play</Anchor>}
            </Group>
          </>
        )}

        <Text size="xs" color="dimmed" align="center">
          Tap above to sign in automatically to your Captiva account.
        </Text>
      </Stack>
    </Box>
  );
}

export default LoginLinkLanding;
